import React, { FC, useCallback, useEffect, useState } from 'react';
import UIDuck from '../../redux/commonDucks/ui.duck';
import { Dispatch } from 'redux';
import Icon, { Icons } from '../Icon';
import { connect, useSelector } from 'react-redux';
import { ModalDeleteDeal } from './ModalDeleteDeal';
import ModalFinancialSummary from './ModalFinancialSummary';
import { ModalDealerFeesPostalCode } from './ModalDealerFeesPostalCode';
import { ModalDealerFeesPostalCodeError } from './ModalDealerFeesPostalCodeError';
import { ModalOrderReviewError } from './ModalOrderReviewError';
import { ModalOrderReviewRedirectError } from './ModalOrderReviewRedirectError';
import InvalidConfigurationModalContent from './InvalidConfigurationModalContent';
import ModalFinancialSummaryConfiguration from './ModalFinancialSummaryConfiguration';
import ModalCarOptionDetail from './ModalCarOptionDetail';
import ModalShareConfigurationDealerCode from './ModalShareConfigurationDealerCode';
import ModalDealerSearch from './ModalDealerSearch';
import ModalTiresDetail from './ModalTiresDetail';
import useTranslations from '@hooks/useTranslations';
import ModalMissingParts from './ModalMissingParts';
import ModalFinanceLegal from './ModalFinanceLegal';
import { IModalCallbacks, Redux } from '../../redux';
import ModalLeadForm from '../ModalLeadForm';
import ModalPaymentMock from './ModalPaymentMock';
import ModalStockVehicleUnavalaible from './ModalStockVehicleUnavalaible';
import { ModalFeatureSwitches } from './ModalFeatureSwitches';
import { ModalShareConfigurationBasket } from './ModalShareConfigurationBasket';
import { ModalShareConfiguration } from './ModalShareConfiguration';
import { ModalScrappage } from './ModalScrappage';
import { ModalEngineCompare } from '@components/Modal/ModalComparators/ModalEngineCompare/ModalEngineCompare';
import AccessibleButton from '@components/Button/AccessibleButton';
import ModalShareOfferSummary from './ModalShareOfferSummary';
import ModalDisableCheckout from '@components/Modal/ModalDisableCheckout';
import { FocusPlaceholder } from './ModalStyled';
import { ModalPostalCode } from '@components/Modal/ModalPostalCode';
import FilterDuck from '../../redux/filters/filter.duck';
import ModalFeatures from '@components/Modal/ModalFeatures';
import ModalTrimCompare from '@components/Modal/ModalComparators/ModalTrimCompare';
import ModalFeature from '@components/Modal/ModalFeatureDetail';

export interface IModal {
    isModalOpen: boolean;
    modalData: any;
    modalType: string;
    dispatch: Dispatch;
    className?: string;
    modalCallbacks: IModalCallbacks;
}

export interface IState {
    isShowTitle: boolean;
    isShowBackButton: boolean;
    onBackClick?: () => void;
    onCloseModal?: ({ cb }?: { cb?: () => void }) => void;
}

const Modal: FC<IModal> = ({ isModalOpen, modalData, modalCallbacks, modalType, dispatch, className }) => {
    const { t } = useTranslations();
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));

    const closeModal = useCallback(() => {
        modalCallbacks?.onClose?.();
        dispatch(UIDuck.closeModal());
    }, [modalCallbacks, dispatch, geoLocation]);

    const initialModalState: IState = {
        isShowTitle: true,
        isShowBackButton: false,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onBackClick: () => {},
        onCloseModal: ({ cb }) => {
            closeModal();
            cb?.();
        },
    };

    const [modal, setModal] = useState<IState>(initialModalState);

    const clickBuster = useCallback(
        (event) => {
            if (event.currentTarget === event.target) {
                event.preventDefault();
                closeModal();
            }
        },
        [closeModal]
    );

    const onSubmit = useCallback(
        (...args: any[]) => {
            modalCallbacks?.onClick('submit', ...args);
            dispatch(UIDuck.closeModal());
        },
        [modalCallbacks, dispatch]
    );

    // close modal if ESC key is pressed
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };
        if (isModalOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModalOpen, closeModal]);

    const getContext = (type: string) => {
        switch (type) {
            case UIDuck.MODAL_TYPES.DELETE_DEAL:
                return <ModalDeleteDeal onSubmit={onSubmit} onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.ENGINE_COMPARE:
                return (
                    <ModalEngineCompare
                        disableSelection={modalData.disableSelection}
                        motorizations={modalData.motorizations}
                        onSubmit={onSubmit}
                        setModal={setModal}
                        modal={modal}
                        displayBackBtn={modalData.displayBackBtn}
                    />
                );
            case UIDuck.MODAL_TYPES.SCRAPPAGE:
                return <ModalScrappage onSubmit={onSubmit} />;
            case UIDuck.MODAL_TYPES.EXAMPLE:
                return <div>{modalData.title}</div>;
            case UIDuck.MODAL_TYPES.DEALER_FEES_POSTAL_CODE:
                return <ModalDealerFeesPostalCode onSubmit={onSubmit} onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.DEALER_FEES_POSTAL_CODE_ERROR:
                return <ModalDealerFeesPostalCodeError onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.SHARE_CONFIGURATION_DEALER_CODE:
                return <ModalShareConfigurationDealerCode {...modalData} closeModal={closeModal} />;
            case UIDuck.MODAL_TYPES.DISABLE_CHECKOUT:
                return <ModalDisableCheckout closeModal={closeModal} />;
            case UIDuck.MODAL_TYPES.DEALER_SEARCH:
                return <ModalDealerSearch {...modalData} onSubmit={onSubmit} />;
            case UIDuck.MODAL_TYPES.ORDER_REVIEW_ERROR:
                return <ModalOrderReviewError onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.ORDER_REVIEW_REDIRECT_ERROR:
                return <ModalOrderReviewRedirectError onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY:
                return <ModalFinancialSummary {...modalData} />;
            case UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY_CONFIGURATION:
                return (
                    <ModalFinancialSummaryConfiguration
                        {...{ error: modalData?.error, data: modalData?.financialData?.data }}
                        setModal={setModal}
                        modal={modal}
                    />
                );
            case UIDuck.MODAL_TYPES.CAR_OPTION_DETAIL:
                return <ModalCarOptionDetail {...modalData} />;
            case UIDuck.MODAL_TYPES.POSTAL_CODE:
                return <ModalPostalCode {...modalData} setModal={setModal} onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.INVALID_CONFIGURATION:
                return <InvalidConfigurationModalContent data={modalData} />;
            case UIDuck.MODAL_TYPES.MISSING_PARTS:
                return (
                    <ModalMissingParts
                        message={modalData.message}
                        additionalMessage={modalData.additionalMessage}
                        buttonText={modalData.buttonText}
                        onClick={closeModal}
                    />
                );
            case UIDuck.MODAL_TYPES.FINANCE_LEGAL:
                return (
                    <ModalFinanceLegal
                        message={modalData.message}
                        additionalMessage={modalData.additionalMessage}
                        buttonText={modalData.buttonText}
                        onClick={closeModal}
                    />
                );
            case UIDuck.MODAL_TYPES.LEAD_FORM:
                return <ModalLeadForm {...modalData} />;
            case UIDuck.MODAL_TYPES.SHARE_OFFER_SUMMARY:
                return <ModalShareOfferSummary {...modalData} />;
            case UIDuck.MODAL_TYPES.PAYMENT_MOCK:
                return <ModalPaymentMock onClick={modalCallbacks.onClick} onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.STOCK_CAR_UNAVAILABLE:
                return <ModalStockVehicleUnavalaible onClose={closeModal} {...modalData} />;
            case UIDuck.MODAL_TYPES.FEATURES:
                return <ModalFeatures onClose={closeModal} {...modalData} />;
            case UIDuck.MODAL_TYPES.TRIM_COMPARE:
                return <ModalTrimCompare onClose={closeModal} {...modalData} />;
            case UIDuck.MODAL_TYPES.FEATURE:
                return <ModalFeature onClose={closeModal} {...modalData} />;
            case UIDuck.MODAL_TYPES.FEATURE_SWITCHES:
                return <ModalFeatureSwitches onClose={closeModal} />;
            case UIDuck.MODAL_TYPES.SHARE_CONFIGURATION:
                return <ModalShareConfiguration onClose={closeModal} email={modalData?.email} />;
            case UIDuck.MODAL_TYPES.SHARE_CONFIGURATION_BASKET:
                return <ModalShareConfigurationBasket {...modalData} />;
            case UIDuck.MODAL_TYPES.TIRES_DETAIL:
                return <ModalTiresDetail {...modalData} />;
            default:
                return null;
        }
    };
    const getTitle = (type: string) => {
        switch (type) {
            case UIDuck.MODAL_TYPES.DELETE_DEAL:
                return t('dialog.deleteDeal.title');
            case UIDuck.MODAL_TYPES.EXAMPLE:
                return 'Le xamplee';
            case UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY:
                return 'DETAIL DU FINANCEMENT';
            case UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY_CONFIGURATION:
                return modalData.data?.globalTexts?.detailTitle || t('financial.detailTitle');
            case UIDuck.MODAL_TYPES.INVALID_CONFIGURATION:
                return t('modal.configurator.invalidConfiguration.title');
            case UIDuck.MODAL_TYPES.MISSING_PARTS:
                return modalData.title;
            case UIDuck.MODAL_TYPES.LEAD_FORM:
                return t('configurator.interestedBox3.link');
            case UIDuck.MODAL_TYPES.PAYMENT_MOCK:
                return t('checkout.payment.modal.title');
            case UIDuck.MODAL_TYPES.FEATURE_SWITCHES:
                return t('featureSwitches.modal.title');
            case UIDuck.MODAL_TYPES.TIRES_DETAIL:
                return t('technicalFeatures.tiresDetailModal.title');
            case UIDuck.MODAL_TYPES.FINANCE_LEGAL:
                return modalData.title;
            default:
                return null;
        }
    };

    const getExtraClass = (type: string) => {
        switch (type) {
            case UIDuck.MODAL_TYPES.TIRES_DETAIL:
                return 'tires-detail';
            case UIDuck.MODAL_TYPES.FINANCE_LEGAL:
                return 'finance-legal';
            case UIDuck.MODAL_TYPES.ENGINE_COMPARE:
                return 'engines';
            case UIDuck.MODAL_TYPES.TRIM_COMPARE:
                return 'trim-compare';
            default:
                return null;
        }
    };

    const isPermanentPopup = (type: string) => {
        switch (type) {
            case UIDuck.MODAL_TYPES.SCRAPPAGE:
            case UIDuck.MODAL_TYPES.FINANCE_LEGAL:
            case UIDuck.MODAL_TYPES.POSTAL_CODE:
                return true;
            default:
                return false;
        }
    };

    const isWithoutOverlay = (type: string) => {
        switch (type) {
            case UIDuck.MODAL_TYPES.FINANCE_LEGAL:
                return 'no-overlay';
            default:
                return '';
        }
    };

    return (
        <div className={className}>
            {isModalOpen && (
                <div
                    className={`modalWindow ${isWithoutOverlay(modalType)}`}
                    onClick={!isPermanentPopup(modalType) && clickBuster}
                >
                    <div className={`modalWindow__wrap ${getExtraClass(modalType)}`}>
                        <div className="modalWindow__wrap__header">
                            {modal.isShowBackButton && (
                                <button className="modalWindow__wrap__header__back" onClick={modal.onBackClick}>
                                    <Icon name={Icons.chevronLeftV2} width={45} height={45} />
                                </button>
                            )}
                            {modal.isShowTitle && (
                                <p className="modalWindow__wrap__header__title">{getTitle(modalType)}</p>
                            )}
                            {modalType !== UIDuck.MODAL_TYPES.SCRAPPAGE && (
                                <>
                                    <FocusPlaceholder autoFocus type="button" />
                                    <AccessibleButton
                                        className={'modalWindow__wrap__header__closeBtn'}
                                        onClick={modal.onCloseModal}
                                        data-testid="TESTING_CLOSE_MODAL"
                                    >
                                        &#10005;
                                    </AccessibleButton>
                                </>
                            )}
                        </div>
                        <div className="modalWindow__wrap__modalBody">{getContext(modalType)}</div>
                    </div>
                </div>
            )}
        </div>
    );
};
const mapStateToProps = (state: any) => {
    return {
        isModalOpen: UIDuck.isModalOpen(state),
        modalData: UIDuck.getModalData(state),
        modalCallbacks: UIDuck.getModalCallbacks(state),
        modalType: UIDuck.getModalType(state),
    };
};

export default connect(mapStateToProps)(Modal);
